// The guided tour of QMS Control for the people who look after policies: how a
// document is edited in the app, approved, and taken live.
//
// Steps point at the Documents tab via [data-tour] attributes. The editor is a
// full-screen surface that opens from Edit content, so the steps about it are
// written to read standalone and the engine shows them centred (its documented
// fallback for a missing target).

export const QMS_CONTROL_TOUR_ID = 'qms-control';

export const qmsControlTourSteps = [
  {
    title: 'Policies are edited here now',
    body: [
      'A version of a policy used to be a PDF and a Word file uploaded together. It can now be content written or imported in the app, with a plain-English Overview alongside the Full version. This tour shows the path from an edit to a live version.',
    ],
  },
  {
    title: 'New documents',
    target: '[data-tour="qms-new-document"]',
    body: [
      'A document is the thing with a number, an owner and a review period. Its content lives in versions. Create the document first, then give it a version.',
      'Announcement is a document type alongside policies and procedures: a notice everyone must read. Tick Requires acknowledgement and, when a version goes live, every member of staff gets an Action to read and acknowledge it.',
    ],
  },
  {
    title: 'Edit content',
    target: '[data-tour="qms-edit-content"]',
    body: [
      'Edit content copies the live version into a new draft, suggests the next version number (1.0 to 1.1), and opens the editor. Nothing changes for staff until the draft is approved and goes live.',
      'If a draft is already open, the same button continues it. Only one draft is open per document at a time.',
    ],
  },
  {
    title: 'The editor',
    body: [
      'Two tabs on the same draft: Full version (the controlled wording, which is what people acknowledge) and Overview (the summary staff read first). Changes save automatically; the header shows when.',
      'The toolbar is deliberately small: headings, lists, links, tables and images. Every policy gets the same house rendering.',
    ],
  },
  {
    title: 'Suggest an overview',
    body: [
      'On the Overview tab, Suggest a draft writes a first version from the full text. It is only a suggestion until it is saved: read it as HR would, correct it, and it is yours.',
    ],
  },
  {
    title: 'Import from Word',
    target: '[data-tour="qms-new-version"]',
    body: [
      'New version offers three ways in: import a Word document (converted in the browser, with the Word file kept as the audit copy), write in the app, or upload a PDF and Word pair for forms and spreadsheets that are not prose.',
    ],
  },
  {
    title: 'Approve and go live',
    target: '[data-tour="qms-go-live"]',
    body: [
      'Submit for approval marks the draft as awaiting approval. Someone other than its author then takes it live; an administrator can override that, and the override is recorded.',
      'Going live asks whether everyone must re-acknowledge. Untick that for a correction that does not change what staff are agreeing to, and the existing acknowledgements are kept.',
    ],
  },
  {
    title: 'Acknowledgements',
    target: '[data-tour="qms-acknowledgements"]',
    body: [
      'For a document that requires acknowledgement, this opens the register: who has acknowledged the live version, who has not yet, and who is exempt. The Action in each person\'s queue clears itself as they read.',
    ],
  },
  {
    title: 'Deleting a document',
    target: '[data-tour="qms-delete"]',
    body: [
      'Archiving takes a document out of the library and keeps its history. Deleting does not: it removes every version, every acknowledgement record and the uploaded files, and it cannot be undone.',
      'Tick the boxes on the left to delete several at once. The dialog lists what is going and asks you to type DELETE before it will do anything.',
    ],
  },
  {
    title: 'History',
    target: '[data-tour="qms-history"]',
    body: [
      'Every version stays in the history with who wrote it, who approved it, and who has acknowledged it. Drafts can be previewed, edited or discarded from here; live and superseded versions cannot be changed.',
    ],
  },
];
